import { bgnToEur } from './currency';

// Shape of `reliability/latest.json`, built by backend/src/reliability/build-reliability.js from the
// ADAC breakdown statistics and the TÜV-Report, joined with mobile.bg prices and autodata24 body types.

export type TuevListType = 'best' | 'worst' | null;

export interface TuevObservation {
  /** % of cars failing the roadworthiness inspection with significant/dangerous defects. */
  defectRate: number;
  rank: number | null;
  list: TuevListType;
}

export interface ReliabilityModel {
  id: string;
  brandKey: string | null;
  modelKey: string | null;
  make: string;
  model: string;
  sourceNames: string[];
  bodyTypes: number[];
  price: { meanBgn: number; adCount: number; carPathModel: string } | null;
  /** Breakdowns per 1,000 vehicles, keyed by first-registration year. */
  adac: { byRegistrationYear: Record<string, number> } | null;
  tuev: { byAgeClass: Record<string, TuevObservation> } | null;
  /** 0-100, 50 = as reliable as the average car of the same age. */
  score: number | null;
  scoreBreakdown: { adac: number | null; tuev: number | null };
  observations: number;
}

export interface ReliabilitySourceInfo {
  reportYear: number | null;
  dataYear: number | null;
  url: string | null;
  coverage: 'full' | 'partial';
  origin: 'scraped' | 'manual';
  note: string | null;
  ageClasses?: string[];
  classAverages?: Record<string, number>;
  entryCount: number;
}

export interface ReliabilityDocument {
  schemaVersion: number;
  generatedAt: string;
  priceSnapshot: string | null;
  sources: { adac: ReliabilitySourceInfo | null; tuev: ReliabilitySourceInfo | null };
  models: ReliabilityModel[];
}

// Body types are the backend's CategoryType enum values (backend/src/mobileCrawler/types.js).
// LIMOUSINE (8, stretch limousines) is deliberately not offered as a category.
export interface BodyTypeCategory {
  id: string;
  label: string;
  types: number[];
}

export const BODY_TYPE_CATEGORIES: BodyTypeCategory[] = [
  { id: 'suv', label: 'SUV / Crossover', types: [1] },
  { id: 'sedan', label: 'Sedan', types: [7] },
  { id: 'hatchback', label: 'Hatchback', types: [9] },
  { id: 'estate', label: 'Estate (Combi)', types: [3] },
  { id: 'minivan', label: 'Minivan', types: [5] },
  { id: 'van', label: 'Van / MPV', types: [0] },
  { id: 'coupe', label: 'Coupe', types: [4] },
  { id: 'cabrio', label: 'Convertible', types: [2] },
  { id: 'pickup', label: 'Pickup', types: [6] },
];

export function bodyTypeLabel(type: number): string | null {
  return BODY_TYPE_CATEGORIES.find((c) => c.types.includes(type))?.label ?? null;
}

export interface PriceBand {
  id: string;
  label: string;
  /** Inclusive lower / exclusive upper bound of the average listing price, in EUR. */
  minEur: number;
  maxEur: number;
}

export const PRICE_BANDS: PriceBand[] = [
  { id: 'under-5k', label: 'Under €5,000', minEur: 0, maxEur: 5000 },
  { id: '5k-10k', label: '€5,000 – €10,000', minEur: 5000, maxEur: 10000 },
  { id: '10k-20k', label: '€10,000 – €20,000', minEur: 10000, maxEur: 20000 },
  { id: '20k-35k', label: '€20,000 – €35,000', minEur: 20000, maxEur: 35000 },
  { id: '35k-plus', label: '€35,000+', minEur: 35000, maxEur: Infinity },
];

export const TOP_N = 10;

export function averagePriceEur(model: ReliabilityModel): number | null {
  return model.price ? bgnToEur(model.price.meanBgn) : null;
}

/** Scored models that exist in our catalog, best first. */
export function rankableModels(models: ReliabilityModel[]): ReliabilityModel[] {
  return models
    .filter((m): m is ReliabilityModel & { score: number } => m.score !== null && m.brandKey !== null)
    .sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
}

export interface RankedCategory<C> {
  category: C;
  models: ReliabilityModel[];
  /** How many scored models the category has in total (the list shows at most TOP_N). */
  total: number;
}

export function topModelsByPriceBand(models: ReliabilityModel[], limit = TOP_N): RankedCategory<PriceBand>[] {
  const ranked = rankableModels(models);
  return PRICE_BANDS.map((band) => {
    const inBand = ranked.filter((m) => {
      const price = averagePriceEur(m);
      return price !== null && price >= band.minEur && price < band.maxEur;
    });
    return { category: band, models: inBand.slice(0, limit), total: inBand.length };
  });
}

export function topModelsByBodyType(models: ReliabilityModel[], limit = TOP_N): RankedCategory<BodyTypeCategory>[] {
  const ranked = rankableModels(models);
  return BODY_TYPE_CATEGORIES.map((category) => {
    const inCategory = ranked.filter((m) => m.bodyTypes.some((t) => category.types.includes(t)));
    return { category, models: inCategory.slice(0, limit), total: inCategory.length };
  });
}

export type ReliabilityRating = 'excellent' | 'good' | 'average' | 'below-average' | 'poor';

export function ratingForScore(score: number): ReliabilityRating {
  if (score >= 70) return 'excellent';
  if (score >= 58) return 'good';
  if (score >= 45) return 'average';
  if (score >= 33) return 'below-average';
  return 'poor';
}

export const RATING_LABELS: Record<ReliabilityRating, string> = {
  excellent: 'Excellent',
  good: 'Good',
  average: 'Average',
  'below-average': 'Below average',
  poor: 'Poor',
};

/** Mean ADAC breakdown rate across the model's registration years (null without ADAC data). */
export function averageBreakdownRate(model: ReliabilityModel): number | null {
  const values = Object.values(model.adac?.byRegistrationYear ?? {});
  return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
}

/** All registration years present in the ADAC data, newest first. */
export function adacRegistrationYears(models: ReliabilityModel[]): string[] {
  const years = new Set(models.flatMap((m) => Object.keys(m.adac?.byRegistrationYear ?? {})));
  return [...years].sort((a, b) => Number(b) - Number(a));
}

export interface RateEntry {
  model: ReliabilityModel;
  value: number;
}

/** Lowest (`best`) or highest (`worst`) ADAC breakdown rates for one registration year. */
export function adacRanking(models: ReliabilityModel[], year: string, order: 'best' | 'worst', limit = TOP_N): RateEntry[] {
  return models
    .flatMap((model) => {
      const value = model.adac?.byRegistrationYear[year];
      return value === undefined ? [] : [{ model, value }];
    })
    .sort((a, b) => (order === 'best' ? a.value - b.value : b.value - a.value) || a.model.id.localeCompare(b.model.id))
    .slice(0, limit);
}

/** Lowest (`best`) or highest (`worst`) TÜV defect rates for one age class. */
export function tuevRanking(models: ReliabilityModel[], ageClass: string, order: 'best' | 'worst', limit = TOP_N): RateEntry[] {
  return models
    .flatMap((model) => {
      const observation = model.tuev?.byAgeClass[ageClass];
      return observation ? [{ model, value: observation.defectRate }] : [];
    })
    .sort((a, b) => (order === 'best' ? a.value - b.value : b.value - a.value) || a.model.id.localeCompare(b.model.id))
    .slice(0, limit);
}

/** The /cars/:make/:model page with this model's price details, or null if it isn't in our catalog. */
export function carDetailsPath(model: ReliabilityModel): string | null {
  if (!model.brandKey) {
    return null;
  }
  const modelSegment = model.price?.carPathModel ?? model.model;
  return `/cars/${encodeURIComponent(model.make)}/${encodeURIComponent(modelSegment)}`;
}

export function displayName(model: ReliabilityModel): string {
  return `${model.make} ${model.model}`.trim();
}

/** The reliability entry for a /cars/:make/:model page, matched the same way `carDetailsPath` builds that URL. */
export function findReliabilityModel(models: ReliabilityModel[], make: string, model: string): ReliabilityModel | null {
  const wantedMake = make.toLowerCase();
  const wantedModel = model.toLowerCase();
  return (
    models.find(
      (m) =>
        m.brandKey !== null &&
        m.make.toLowerCase() === wantedMake &&
        (m.price?.carPathModel ?? m.model).toLowerCase() === wantedModel,
    ) ?? null
  );
}
